/**
 * FmSurfaceGallery — the `#surface-glass-deep` specimen: a light workspace
 * stage holding glass-deep surfaces (an answer card with the selector, a deep
 * inspector, the section states), the paper tone beside light and deep, and the
 * untouched-operation grey on each ground. Every contrast it prints is measured
 * from the rendered page, so the numbers move with the tokens.
 */
import React, { useCallback, useRef, useState } from 'react';
import { FmContentWidth } from './FmLayout';
import { FmHeading, FmStack, FmText } from './FmComposition';
import { FmGalleryIdentity } from './FmGalleryIdentity';
import { FmSelector, type FmSelectorItem } from './FmSelector';
import { FmSurface } from './FmSurface';
import { FmValueList } from './FmValueList';
import { FmSection } from './sections/FmSection';
import type { FmSection as FmSectionDescriptor } from './sections/section.types';
import {
  compositeOver,
  contrastRatio,
  effectiveBackground,
  parseComputedColor,
  resolveRoleColor,
  type FmRgba,
} from './fmContrast';
import { ToneBands, UntouchedGrounds } from './FmSurfaceGalleryEvidence';
import {
  GRAPHIC,
  TEXT_AA,
  useMeasured,
  valueItems,
  type FmSurfaceGalleryCopy,
  type Measured,
  type RoleKey,
} from './surfaceGalleryMeasure';
import './FmSurfaceGallery.css';

export type { FmSurfaceGalleryCopy } from './surfaceGalleryMeasure';

interface SelectorSpecimen {
  label: string;
  items: readonly FmSelectorItem[];
  initial: string;
}

/** Role token, its threshold, and the token behind it when it is not the surface. */
const ROLES: ReadonlyArray<{ key: RoleKey; token: string; min: number; on?: string }> = [
  { key: 'text', token: '--fm-d-fg', min: TEXT_AA },
  { key: 'secondary', token: '--fm-d-fg-2', min: TEXT_AA },
  { key: 'bandText', token: '--fm-band-fg', min: TEXT_AA },
  { key: 'bandMuted', token: '--fm-band-muted', min: TEXT_AA },
  { key: 'figureText', token: '--fm-figure-surface-fg', min: TEXT_AA, on: '--fm-figure-surface' },
  {
    key: 'figureMuted',
    token: '--fm-figure-surface-muted',
    min: TEXT_AA,
    on: '--fm-figure-surface',
  },
  { key: 'selected', token: '--fm-on-dark-selected-fg', min: TEXT_AA, on: '--fm-on-dark-selected' },
  { key: 'selectedRing', token: '--fm-on-dark-selected-ring', min: GRAPHIC },
  { key: 'untouched', token: '--fm-operation-untouched', min: GRAPHIC },
];

export interface FmSurfaceGalleryProps {
  title: string;
  intro: string;
  answer: FmSectionDescriptor;
  inspector: FmSectionDescriptor;
  states: readonly FmSectionDescriptor[];
  evidence: FmSectionDescriptor;
  lens: SelectorSpecimen;
  basemap: SelectorSpecimen;
  copy: FmSurfaceGalleryCopy;
}

function roleRows(surface: HTMLElement, copy: FmSurfaceGalleryCopy): Measured[] {
  const ground = effectiveBackground(surface);
  return ROLES.map(({ key, token, min, on }) => {
    const behind: FmRgba = on ? compositeOver(resolveRoleColor(surface, on), ground) : ground;
    return {
      id: key,
      label: `${copy.roles[key]} · ${token}`,
      ratio: contrastRatio(resolveRoleColor(surface, token), behind),
      min,
    };
  });
}

function hasOwnText(element: Element): boolean {
  return [...element.childNodes].some(
    (node) => node.nodeType === Node.TEXT_NODE && (node.textContent ?? '').trim().length > 0,
  );
}

/** The lowest contrast of any visible text inside the surfaces. */
function lowestRenderedText(surfaces: readonly HTMLElement[]): Measured | null {
  let lowest: Measured | null = null;
  for (const surface of surfaces) {
    for (const element of surface.querySelectorAll('*')) {
      if (!hasOwnText(element) || element.closest('[aria-hidden="true"]')) continue;
      const box = element.getBoundingClientRect();
      if (box.width === 0 || box.height === 0) continue;
      const style = getComputedStyle(element);
      const ink = parseComputedColor(style.color);
      const ratio = contrastRatio(
        { ...ink, a: ink.a * Number(style.opacity) },
        effectiveBackground(element),
      );
      if (lowest === null || ratio < lowest.ratio) {
        lowest = {
          id: 'lowest',
          label: (element.textContent ?? '').trim(),
          ratio,
          min: TEXT_AA,
        };
      }
    }
  }
  return lowest;
}

/**
 * Each rendered ranked-row swatch against the glass it sits on: a data colour
 * is a graphic, so it needs 3:1. The label is the species line of its row.
 */
function swatchRows(surface: HTMLElement): Measured[] {
  const seen = new Set<string>();
  return [...surface.querySelectorAll<HTMLElement>('.fm-ranked-list__swatch')].flatMap((swatch) => {
    const row = swatch.parentElement;
    const species = row?.querySelector('.fm-ranked-list__sublabel')?.textContent?.trim();
    if (!row || !species || seen.has(species)) return [];
    seen.add(species);
    return [
      {
        id: `swatch-${seen.size}`,
        label: species,
        ratio: contrastRatio(
          parseComputedColor(getComputedStyle(swatch).backgroundColor),
          effectiveBackground(row),
        ),
        min: GRAPHIC,
      },
    ];
  });
}

const StageSelectors: React.FC<{ lens: SelectorSpecimen; basemap: SelectorSpecimen }> = ({
  lens,
  basemap,
}) => {
  const [lensValue, setLens] = useState(lens.initial);
  const [basemapValue, setBasemap] = useState(basemap.initial);
  return (
    <div className="fm-surface-gallery__controls">
      {/* The lens reads as a segmented track on a wide card and as wrapping
          chips on a phone, where three options do not fit one track. */}
      <div className="fm-surface-gallery__wide-only">
        <FmSelector
          variant="segmented"
          label={lens.label}
          items={[...lens.items]}
          value={lensValue}
          onChange={setLens}
          data-testid="surface-glass-deep-segmented"
        />
      </div>
      <div className="fm-surface-gallery__phone-only">
        <FmSelector
          variant="chips"
          label={lens.label}
          items={[...lens.items]}
          value={lensValue}
          onChange={setLens}
          data-testid="surface-glass-deep-lens-chips"
        />
      </div>
      <FmSelector
        variant="chips"
        label={basemap.label}
        items={[...basemap.items]}
        value={basemapValue}
        onChange={setBasemap}
        data-testid="surface-glass-deep-chips"
      />
    </div>
  );
};

const Stage: React.FC<FmSurfaceGalleryProps & { answerRef: React.Ref<HTMLDivElement> }> = (
  props,
) => (
  <section
    className="fm-surface-gallery__stage"
    aria-label={props.copy.stageLabel}
    data-testid="surface-glass-deep-stage"
  >
    <div className="fm-surface-gallery__stage-grid">
      <div ref={props.answerRef} className="fm-surface-gallery__answer">
        <FmSurface
          surface="glass-deep"
          as="section"
          aria-label={props.copy.answerLabel}
          data-testid="surface-glass-deep-card"
        >
          <FmStack gap="md">
            <StageSelectors lens={props.lens} basemap={props.basemap} />
            <FmSection
              section={props.answer}
              presentation="embedded"
              hostTone="deep"
              headingLevel={3}
            />
          </FmStack>
        </FmSurface>
      </div>
      <div className="fm-surface-gallery__inspector">
        <FmSurface
          surface="glass-deep"
          as="aside"
          aria-label={props.copy.inspectorLabel}
          data-testid="surface-glass-deep-inspector-panel"
        >
          <FmSection
            section={props.inspector}
            presentation="embedded"
            hostTone="deep"
            headingLevel={3}
          />
        </FmSurface>
      </div>
    </div>
    <FmText size="caption" tone="muted">
      {props.copy.statesLabel}
    </FmText>
    <div className="fm-surface-gallery__states">
      {props.states.map((section) => (
        <FmSurface
          key={section.id}
          surface="glass-deep"
          padding="compact"
          data-testid={`${section.id}-surface`}
        >
          <FmSection section={section} presentation="embedded" hostTone="deep" headingLevel={3} />
        </FmSurface>
      ))}
    </div>
  </section>
);

const GlassDeepMeasurements: React.FC<{
  copy: FmSurfaceGalleryCopy;
  answer: React.RefObject<HTMLDivElement | null>;
}> = ({ copy, answer }) => {
  const measure = useCallback(() => {
    const host = answer.current;
    const surface = host?.querySelector<HTMLElement>('.fm-surface--glass-deep');
    const stage = host?.closest('.fm-surface-gallery__stage');
    if (!surface || !stage) return null;
    const surfaces = [...stage.querySelectorAll<HTMLElement>('.fm-surface--glass-deep')];
    return {
      roles: roleRows(surface, copy),
      lowest: lowestRenderedText(surfaces),
      swatches: swatchRows(surface),
    };
  }, [answer, copy]);
  const measured = useMeasured(measure);
  return (
    <div className="fm-surface-gallery__measures">
      <FmStack gap="xs">
        <FmHeading level={4}>{copy.rolesTitle}</FmHeading>
        <FmValueList
          items={valueItems(copy, measured?.roles ?? null, 'surface-role')}
          density="compact"
          data-testid="surface-glass-deep-roles"
        />
      </FmStack>
      <FmStack gap="lg">
        <FmStack gap="xs">
          <FmHeading level={4}>{copy.renderedTitle}</FmHeading>
          <FmValueList
            items={valueItems(
              copy,
              measured?.lowest ? [measured.lowest] : null,
              'surface-rendered',
            )}
            density="compact"
            data-testid="surface-glass-deep-rendered"
          />
        </FmStack>
        <FmStack gap="xs">
          <FmHeading level={4}>{copy.swatchesTitle}</FmHeading>
          <FmValueList
            items={valueItems(copy, measured?.swatches ?? null, 'surface-swatch')}
            density="compact"
            data-testid="surface-glass-deep-swatches"
          />
        </FmStack>
      </FmStack>
    </div>
  );
};

export const FmSurfaceGallery: React.FC<FmSurfaceGalleryProps> = (props) => {
  const answer = useRef<HTMLDivElement>(null);
  return (
    <section
      className="fm-surface-gallery"
      id="surface-glass-deep"
      data-testid="surface-glass-deep-gallery"
    >
      <FmContentWidth width="content" padY="lg">
        <FmStack gap="sm">
          <FmHeading level={2}>{props.title}</FmHeading>
          <FmText tone="muted">{props.intro}</FmText>
          <FmGalleryIdentity
            vocabulary="surface"
            names={['glass-deep']}
            label={props.copy.answerLabel}
            useFor={'FmSurface surface="glass-deep" · FmSelector tone="on-dark"'}
            data-testid="surface-glass-deep-identity"
          />
        </FmStack>
      </FmContentWidth>
      <FmContentWidth width="content" padY="sm">
        <FmStack gap="md">
          <Stage {...props} answerRef={answer} />
          <GlassDeepMeasurements copy={props.copy} answer={answer} />
        </FmStack>
      </FmContentWidth>
      <ToneBands evidence={props.evidence} copy={props.copy} />
      <UntouchedGrounds copy={props.copy} />
    </section>
  );
};

FmSurfaceGallery.displayName = 'FmSurfaceGallery';
